import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import {
  resolveInitialTheme,
  writeStoredTheme,
  type Theme,
} from "@/shared/utils/themeStorage";

export type { Theme };

interface ThemeProviderProps {
  children: React.ReactNode;
  /** Usado só quando não há preferência salva nem do dispositivo nem do SO. */
  defaultTheme?: Theme;
}

const ThemeContext = createContext<{
  theme: Theme;
  setTheme: (theme: Theme) => void;
}>({
  theme: "dark",
  setTheme: () => null,
});

export function ThemeProvider({
  children,
  defaultTheme = "dark",
}: ThemeProviderProps) {
  // Inicialização preguiçosa: o tema já nasce resolvido a partir do que está
  // salvo no dispositivo. É isso que faz a preferência sobreviver ao logoff —
  // antes, o estado começava fixo e só era corrigido depois que a sessão
  // resolvia, então sem sessão não havia de onde ler.
  const [theme, setThemeState] = useState<Theme>(() =>
    resolveInitialTheme(defaultTheme)
  );

  // Identidade estável: `use-profile-theme` usa setTheme como dependência de
  // efeito, e uma função nova a cada render faria o efeito disparar em loop.
  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    writeStoredTheme(next);
  }, []);

  // useLayoutEffect, não useEffect: a classe precisa estar no DOM antes do
  // browser pintar, senão volta o flash que o script de index.html evita.
  useLayoutEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
  }, [theme]);

  // Garante que o dispositivo tenha a preferência registrada já na primeira
  // visita, para que a resolução seja estável a partir do segundo carregamento.
  useEffect(() => {
    writeStoredTheme(theme);
    // Só na montagem: as trocas seguintes já persistem via setTheme.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");
  return context;
};
